import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Calculator, Plus, Trash } from "@phosphor-icons/react";
import { errMsg } from "@/lib/medicoes";

export function useCatalogo() {
  const [cat, setCat] = useState([]);
  useEffect(() => { api.get("/engenharia/calculadoras").then((r) => setCat(r.data.items)); }, []);
  return cat;
}

export default function Calculadora({ onUse, inicial, unidadeServico, compact }) {
  const cat = useCatalogo();
  const [calcId, setCalcId] = useState(inicial?.calculadora || "");
  const [els, setEls] = useState(inicial?.elementos?.map((e) => ({ descricao: e.descricao, deducao: e.deducao, params: e.params })) || [{ descricao: "", deducao: false, params: {} }]);
  const [ajustes, setAjustes] = useState(inicial?.ajustes || []);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const calc = cat.find((c) => c.id === calcId);
  const grupos = [...new Set(cat.map((c) => c.grupo))];
  const setEl = (i, patch) => setEls(els.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const payload = () => ({ calculadora: calcId, elementos: els, ajustes: ajustes.filter((a) => a.percentual !== "") });
  const calcular = async () => {
    setBusy(true);
    try { const { data } = await api.post("/engenharia/calcular", payload()); setRes(data); }
    catch (e) { setRes(null); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const inp = "tce-input px-2.5 py-2 rounded text-sm w-full";
  return (
    <div className="space-y-4" data-testid="calculadora">
      <select data-testid="calc-select" value={calcId} onChange={(e) => { setCalcId(e.target.value); setRes(null); setEls([{ descricao: "", deducao: false, params: {} }]); }} className={inp}>
        <option value="">Selecione o tipo de serviço…</option>
        {grupos.map((g) => <optgroup key={g} label={g}>{cat.filter((c) => c.grupo === g).map((c) => <option key={c.id} value={c.id}>{`${c.nome} (${c.unidade})`}</option>)}</optgroup>)}
      </select>
      {calc && (
        <>
          <div className="text-xs text-[#94A3B8]">Fórmula: <span className="font-mono text-[#FFDF00]" data-testid="calc-formula">{calc.formula}</span> · resultado em {calc.unidade}
            {unidadeServico && <> · unidade do serviço: <b>{unidadeServico}</b></>}</div>
          <div className="space-y-2">
            {els.map((el, i) => (
              <div key={i} className={`grid gap-2 items-end rounded border p-2 ${el.deducao ? "border-[#EF4444]/50" : "border-[#334155]"}`}
                   style={{ gridTemplateColumns: `minmax(120px,1.4fr) repeat(${calc.params.length}, minmax(80px,1fr)) auto auto` }} data-testid={`calc-el-${i}`}>
                <input value={el.descricao || ""} onChange={(e) => setEl(i, { descricao: e.target.value })} placeholder={`Elemento/ambiente ${i + 1}`} className={inp} />
                {calc.params.map((p) => (
                  <label key={p.k} className="text-[10px] text-[#94A3B8]">{p.label} ({p.unidade})
                    <input data-testid={`calc-el-${i}-${p.k}`} inputMode="decimal" value={el.params?.[p.k] ?? ""} onChange={(e) => setEl(i, { params: { ...el.params, [p.k]: e.target.value } })} className={inp} />
                  </label>
                ))}
                <label className="text-[10px] flex flex-col items-center gap-1">Dedução<input type="checkbox" checked={!!el.deducao} onChange={(e) => setEl(i, { deducao: e.target.checked })} data-testid={`calc-el-${i}-ded`} /></label>
                <button type="button" onClick={() => setEls(els.filter((_, j) => j !== i))} disabled={els.length === 1} className="p-2 text-[#EF4444] disabled:opacity-30"><Trash size={14} /></button>
              </div>
            ))}
            <button type="button" data-testid="calc-add-el" onClick={() => setEls([...els, { descricao: "", deducao: false, params: {} }])} className="text-xs text-[#FFDF00] flex items-center gap-1"><Plus size={12} /> elemento / ambiente / trecho / pavimento</button>
          </div>
          <div className="space-y-2">
            <div className="text-[10px] uppercase-label">Ajustes explícitos (perda, empolamento, contração) — opcionais, revisáveis</div>
            {ajustes.map((a, i) => (
              <div key={i} className="flex gap-2">
                <input value={a.descricao} onChange={(e) => setAjustes(ajustes.map((x, j) => (j === i ? { ...x, descricao: e.target.value } : x)))} placeholder="Ex.: Empolamento do solo" className={inp} />
                <input value={a.percentual} inputMode="decimal" onChange={(e) => setAjustes(ajustes.map((x, j) => (j === i ? { ...x, percentual: e.target.value } : x)))} placeholder="%" className={`${inp} !w-24`} data-testid={`calc-aj-${i}`} />
                <button type="button" onClick={() => setAjustes(ajustes.filter((_, j) => j !== i))} className="text-[#EF4444]"><X size={14} /></button>
              </div>
            ))}
            <button type="button" data-testid="calc-add-ajuste" onClick={() => setAjustes([...ajustes, { descricao: "", percentual: "" }])} className="text-xs text-[#94A3B8] flex items-center gap-1"><Plus size={12} /> ajuste percentual</button>
          </div>
          <button type="button" onClick={calcular} disabled={busy} data-testid="calc-run" className="px-4 py-2.5 rounded-md bg-[#FFDF00] text-[#0F172A] text-sm font-medium flex items-center gap-2 disabled:opacity-50"><Calculator size={16} /> {busy ? "Calculando…" : "Calcular"}</button>
        </>
      )}
      {res && (
        <div className="rounded border border-[#009C3B]/50 bg-[#009C3B]/5 p-4" data-testid="calc-resultado">
          <div className="font-mono text-2xl text-[#22C55E]" data-testid="calc-quantidade">{res.quantidade} {res.unidade}</div>
          <pre className={`text-[11px] whitespace-pre-wrap mt-2 text-[#CBD5E1] ${compact ? "max-h-40" : "max-h-72"} overflow-y-auto`}>{res.memoria.join("\n")}</pre>
          {onUse && <button type="button" data-testid="calc-usar" onClick={() => onUse(payload(), res)} className="mt-3 px-4 py-2 rounded-md bg-[#009C3B] text-white text-sm">Usar esta quantidade</button>}
        </div>
      )}
    </div>
  );
}
